'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Separator, toast } from '@mokoboko/components';

import { useIsEmbed } from '@/contexts/embed';
import { openSitePage } from '@/helpers/embed-navigation';
import { detailPath } from '@/helpers/offering';
import { getPrimaryImage } from '@/lib/entity-image';
import type { OfferingType } from '@/services/offering';
import useClassPackGroups from '@/state/class-pack-groups';
import useSubscriptionGroups from '@/state/subscription-groups';
import { ClassRowCard } from './class-row-card';
import { ShowMoreLink } from './show-more-link';

const VISIBLE_LIMIT = 4;

type PlanRow = {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
  type: Extract<OfferingType, 'pack' | 'subscription'>;
};

// What a money gift card pays for beside its classes (gift cards M1): its
// PUBLIC class packs and subscriptions, as rows that open their own pages,
// under the classes section and in its pattern. Hidden when it lists none.
export const GiftCardPlansSection = ({
  classPackIds,
  subscriptionIds,
}: {
  classPackIds: string[];
  subscriptionIds: string[];
}) => {
  const router = useRouter();
  const isEmbed = useIsEmbed();
  const [expanded, setExpanded] = useState(false);
  const hasPacks = classPackIds.length > 0;
  const hasPlans = subscriptionIds.length > 0;
  const { data: packGroups } = useClassPackGroups({
    include_class_packs: hasPacks,
  });
  const { data: planGroups } = useSubscriptionGroups({
    include_subscriptions: hasPlans,
  });

  const rows = useMemo<PlanRow[]>(() => {
    const packs = new Map(
      (packGroups ?? [])
        .flatMap((group) => group.class_packs ?? [])
        .map((pack) => [pack.class_pack_id, pack] as const)
    );
    const plans = new Map(
      (planGroups ?? [])
        .flatMap((group) => group.subscriptions ?? [])
        .map((plan) => [plan.subscription_id, plan] as const)
    );
    return [
      ...classPackIds.flatMap((id) => {
        const pack = packs.get(id);
        return pack
          ? [
              {
                id,
                name: pack.name,
                slug: pack.slug,
                imageUrl: getPrimaryImage(pack.images)?.url ?? pack.image,
                type: 'pack' as const,
              },
            ]
          : [];
      }),
      ...subscriptionIds.flatMap((id) => {
        const plan = plans.get(id);
        return plan
          ? [
              {
                id,
                name: plan.name,
                slug: plan.slug,
                imageUrl: getPrimaryImage(plan.images)?.url ?? plan.image,
                type: 'subscription' as const,
              },
            ]
          : [];
      }),
    ];
  }, [packGroups, planGroups, classPackIds, subscriptionIds]);

  if (rows.length === 0) return null;

  const open = (row: PlanRow) => {
    const href = detailPath(row.type, row.slug);
    if (!isEmbed) {
      router.push(href);
      return;
    }
    if (!openSitePage(href)) {
      toast.info('Allow pop-ups to open this page.');
    }
  };

  const showsPacks = rows.some((row) => row.type === 'pack');
  const showsPlans = rows.some((row) => row.type === 'subscription');
  const title =
    showsPacks && showsPlans
      ? 'Class packs and memberships it pays for'
      : showsPacks
        ? 'Class packs it pays for'
        : 'Memberships it pays for';
  const visibleRows = expanded ? rows : rows.slice(0, VISIBLE_LIMIT);

  return (
    <div className="flex flex-col gap-6">
      <Separator />
      <section className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="heading-section">{title}</h2>
          <p className="text-sm text-gray-700">
            {showsPlans
              ? 'For a membership, it pays the first payment'
              : 'The person you give it to chooses at checkout'}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-4">
            {visibleRows.map((row) => (
              <ClassRowCard
                key={row.id}
                name={row.name}
                imageUrl={row.imageUrl}
                onClick={() => open(row)}
              />
            ))}
          </div>

          {rows.length > VISIBLE_LIMIT ? (
            <ShowMoreLink
              expanded={expanded}
              hiddenCount={rows.length - VISIBLE_LIMIT}
              onToggle={() => setExpanded((value) => !value)}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
};
